import type {
  TenantAnalyticsAdapter,
  TenantAnalyticsEnvelope,
  TenantPurchaseEvent,
} from '../types';
import {
  type GoogleAdsPublicConfig,
  parseGoogleAdsPublicConfig,
} from './config';

export interface GoogleAdsPurchaseParameters {
  readonly send_to: string;
  readonly value: number;
  readonly currency: string;
  readonly transaction_id: string;
}

export type GoogleAdsEventSink = (
  eventName: 'conversion',
  parameters: GoogleAdsPurchaseParameters,
) => void | Promise<void>;

export interface CreateGoogleAdsAdapterOptions {
  readonly publicConfig: unknown;
  readonly send: GoogleAdsEventSink;
  readonly id?: string;
  readonly enabled?: boolean;
}

export function createGoogleAdsAdapter(
  options: CreateGoogleAdsAdapterOptions,
): TenantAnalyticsAdapter {
  const publicConfig = requirePublicConfig(options.publicConfig);

  if (typeof options.send !== 'function') {
    throw new Error('Google Ads requires an event sink.');
  }

  const send = options.send;

  return Object.freeze({
    id: options.id ?? `google_ads:${publicConfig.conversionId}`,
    provider: 'google_ads',
    requiredConsent: Object.freeze(['advertising'] as const),
    enabled: options.enabled !== false,
    deliver: async (envelope: TenantAnalyticsEnvelope) => {
      if (envelope.event.name !== 'purchase') {
        return;
      }

      requireSafeBrowserDocumentLocation(envelope);

      await send('conversion', mapPurchase(envelope.event, publicConfig));
    },
  });
}

function mapPurchase(
  event: TenantPurchaseEvent,
  publicConfig: GoogleAdsPublicConfig,
): GoogleAdsPurchaseParameters {
  return Object.freeze({
    send_to: `${publicConfig.conversionId}/${publicConfig.conversionLabel}`,
    value: event.value,
    currency: event.currency,
    transaction_id: event.transactionId,
  });
}

// Same guard as the Meta/TikTok/Snap adapters: a conversion tag reads the
// live document.location as page context, so a purchase must never be sent
// while the real payment-return URL (booking id + provider params) is
// unmasked. The runtime records the throw as a failed delivery and retries.
function requireSafeBrowserDocumentLocation(envelope: TenantAnalyticsEnvelope) {
  if (!envelope.event.browserDocumentLocationSafe) {
    throw new Error(
      'Google Ads delivery suppressed for an unsafe browser document location.',
    );
  }
}

function requirePublicConfig(value: unknown): GoogleAdsPublicConfig {
  const publicConfig = parseGoogleAdsPublicConfig(value);

  if (!publicConfig) {
    throw new Error('Invalid Google Ads public configuration.');
  }

  return publicConfig;
}
